import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { PageHeader } from "../../layout/AppLayout";
import { ErpSelect, ErpTable, toOptions } from "../../components/erp";
import { errorText } from "../../api/client";
import { csDropdownsApi, stockSearchApi, StockSearchRow } from "./api";

/** Port of v3/ColdStore/store-stock-search.php - "current stock" listing, not filtered by
 * StockStatus at all (shows any remaining balance, orthogonal to the approval workflow).
 * NOTE: the unfiltered query joins ~13.5k live rows across 6 tables (~30s) - the React port gates
 * the search behind an explicit "Search" click (button-triggered, not auto-run on mount) so the
 * page never appears frozen; this is a frontend-only UX safeguard, not a backend business change. */
export default function StockSearchPage() {
  const [filters, setFilters] = useState({ ColdstoreID: "", VarietyID: "", TenantID: "" });
  const [searched, setSearched] = useState(false);
  const dd = useQuery({ queryKey: ["coldstore", "dropdowns"], queryFn: csDropdownsApi.get, staleTime: 5 * 60_000 });
  const coldstores = useMemo(() => toOptions(dd.data?.Data?.ColdStores, (c) => c.StoreID, (c) => c.StoreName), [dd.data]);
  const tenants = useMemo(() => toOptions(dd.data?.Data?.StockBelongsTo, (t) => t.TenantID, (t) => t.TenantName), [dd.data]);
  const varieties = dd.data?.Data?.Variety || [];
  const list = useQuery({ queryKey: ["coldstore", "stock-search", filters], enabled: searched, queryFn: () => stockSearchApi.search(filters) });

  return (
    <>
      <PageHeader title="Stock Search" location="ColdStore" />
      <div className="card mb-3"><div className="card-body">
        <div className="row">
          <div className="col-md-3 mb-3"><label className="form-label">Coldstore</label>
            <ErpSelect id="stock-coldstore" value={filters.ColdstoreID} onChange={(v) => setFilters((s) => ({ ...s, ColdstoreID: v }))} options={coldstores} testId="stock-coldstore" /></div>
          <div className="col-md-3 mb-3"><label className="form-label">Variety</label>
            <select className="form-select" value={filters.VarietyID} onChange={(e) => setFilters((s) => ({ ...s, VarietyID: e.target.value }))} data-testid="stock-variety">
              <option value="">All</option>{varieties.map((o) => <option key={o.VarietyID} value={o.VarietyID}>{o.ShortName}</option>)}
            </select></div>
          <div className="col-md-3 mb-3"><label className="form-label">Stock Holder</label>
            <ErpSelect id="stock-tenant" value={filters.TenantID} onChange={(v) => setFilters((s) => ({ ...s, TenantID: v }))} options={tenants} testId="stock-tenant" /></div>
          <div className="col-md-3 mb-3 d-flex align-items-end">
            <button className="btn btn-primary w-100" data-testid="stock-search-btn" onClick={() => setSearched(true)}>Search</button></div>
        </div>
      </div></div>
      {searched && <div className="card"><div className="card-body">
        <div className="mb-3">
          <strong data-testid="stock-total-count">{list.data?.TotalCount ?? 0} lots</strong>{" "}
          {list.data?.Totals && <span data-testid="stock-totals">| Mcs: {list.data.Totals.TotalMcs} | Loose: {list.data.Totals.TotalLoose} | Kgs: {list.data.Totals.TotalKgs.toFixed(2)}</span>}
        </div>
        <ErpTable<StockSearchRow> testId="stock-search-table" loading={list.isLoading} error={list.isError ? errorText(list.error) : null}
          rows={list.data?.Data || []} columns={[
            { key: "StockID", title: "Stock ID" }, { key: "BarCodeID", title: "Barcode" }, { key: "Variety", title: "Variety" },
            { key: "Grade", title: "Grade" }, { key: "SUPC", title: "SUPC" }, { key: "Mcs", title: "Mcs" }, { key: "Loose", title: "Loose" },
            { key: "Kgs", title: "Kgs" }, { key: "PackingStyle", title: "Packing Style" }, { key: "Store", title: "Store" },
            { key: "Block", title: "Block" }, { key: "RackNumber", title: "Rack" },
          ]} />
      </div></div>}
    </>
  );
}
